/**
 * useNotificationPreferences — A10-7 User preference data hook.
 *
 * Fetches: current user prefs + default channel matrix.
 * Exposes: savePreferences() to replace full set via PUT.
 */

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../lib/apiClient';

export default function useNotificationPreferences() {
  const [preferences, setPreferences] = useState([]);
  const [defaults, setDefaults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saveError, setSaveError] = useState(null);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [prefs, defs] = await Promise.all([
        apiClient.get('/api/v1/aif/notifications/preferences'),
        apiClient.get('/api/v1/aif/notifications/preferences/defaults'),
      ]);
      setPreferences(prefs?.preferences || []);
      setDefaults(defs || null);
    } catch {
      setPreferences([]);
      setDefaults(null);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const savePreferences = useCallback(async (newPreferences) => {
    setSaveError(null);
    try {
      await apiClient.put('/api/v1/aif/notifications/preferences',
        { preferences: newPreferences },
        { entityType: 'notification_preference', queueOnOffline: true },
      );
      setPreferences(newPreferences);
      return true;
    } catch (e) {
      setSaveError(e);
      return false;
    }
  }, []);

  return { preferences, defaults, isLoading, saveError, savePreferences, refetch: fetchAll };
}
